import { useSyncExternalStore } from 'react';

/**
 * Формат показа выбирает ширина экрана, а не человек (3.1.1).
 *
 * Выбора здесь больше нет вовсе, поэтому нет и запомненного значения: раньше
 * `localStorage` хранил `nedelnik.view`, и ключ остался в браузерах — читать
 * его незачем, а чистить нечем (устройство может и не прийти). Он просто
 * лежит и ни на что не влияет.
 */

/**
 * Ширина, ниже которой доски не существует.
 *
 * Доска — это три колонки рядом (3.1.3), и весь её смысл в том, что работу
 * видно сразу в трёх состояниях. На телефоне колонки складываются в одну, и
 * доска превращается в тот же список, только карточками. Поэтому на узком
 * экране формат один — список (3.1.6), а на большом — одна доска (3.1.5).
 */
const NARROW_QUERY = '(max-width: 700px)';

function isNarrow(): boolean {
  return globalThis.matchMedia?.(NARROW_QUERY).matches ?? false;
}

function subscribeNarrow(onChange: () => void): () => void {
  const query = globalThis.matchMedia?.(NARROW_QUERY);
  query?.addEventListener('change', onChange);
  return () => query?.removeEventListener('change', onChange);
}

/**
 * Узкий ли сейчас экран. Через `useSyncExternalStore`, а не через эффект с
 * состоянием: поворот телефона обязан менять ответ сразу, а не после
 * перерисовки по другой причине.
 */
export function useNarrowScreen(): boolean {
  // Серверного рендеринга у нас нет, но третий аргумент обязателен, и `false`
  // — честное умолчание: в среде без `matchMedia` (jsdom) экран не узкий.
  return useSyncExternalStore(subscribeNarrow, isNarrow, () => false);
}

/**
 * Основной указатель — мышь или тачпад: умеет наводиться и попадает точно.
 *
 * Спрашивается основное устройство ввода, а не «есть ли хоть одно такое»
 * (`any-hover`): телефон с подключённой мышью остаётся телефоном. Ноутбук с
 * сенсорным экраном считается мышью — основной ввод там тачпад. Планшет с
 * клавиатурой браузер обычно называет сенсорным, и это правильный исход:
 * кнопки, которые с мышью не нужны, пальцу нужны (3.1.10).
 *
 * Тот же запрос стоит в стилях у галочки на карточке доски: разметка и стили
 * обязаны отвечать на вопрос «мышь ли это» одинаково.
 */
const MOUSE_QUERY = '(hover: hover) and (pointer: fine)';

function isMouse(): boolean {
  return globalThis.matchMedia?.(MOUSE_QUERY).matches ?? false;
}

function subscribeMouse(onChange: () => void): () => void {
  const query = globalThis.matchMedia?.(MOUSE_QUERY);
  query?.addEventListener('change', onChange);
  return () => query?.removeEventListener('change', onChange);
}

/** Мышь ли основной указатель. Подключили мышь к планшету — ответ меняется сразу. */
export function useMousePointer(): boolean {
  // Без `matchMedia` (jsdom) — не мышь: кнопки остаются, и это безопасная
  // сторона ошибки — лишняя кнопка лучше недостающей.
  return useSyncExternalStore(subscribeMouse, isMouse, () => false);
}
